import { icons } from "assets/icons";
import DefaultCell from "components/DataTable/cells/DefaultCell";
import { datatableStyle } from "components/DataTable/DataTableStyling.config";
import { StyledDataTableWrapper } from "components/DataTable/style";
import LoadingScreen from "components/Loading/LoadingScreen";
import { GenericModal } from "components/Modals/GenericModal";
import { RoundedIcon } from "components/RoundedIcon";
import { Tooltip } from "components/Tooltip";
import { useSystemAdminPermissionStore, useWorkgroupsStore } from "contexts/RootStore.context";
import { Content, Horizontal, Vertical } from "gls/lib";
import { observer } from "mobx-react";
import { useMemo } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { colours } from "styling/colours";
import { style } from "typestyle";
import { useWorkgroups } from "../hooks/useWorkgroups";
import { WorkGroup } from "../WorkgroupDto";
import { EditWorkgroupNameModal } from "./EditWorkgroupNameModal";
import { RowActions } from "./RowActions";

const WorkgroupList = observer(() => {
  const { workgroups, workgroupsLoading } = useWorkgroupsStore();
  const { canManageGroupsAndRoles } = useSystemAdminPermissionStore();
  const {
    changeWorkgroupName,
    currentWorkgroup,
    deleteOpen,
    handleEditOnClose,
    handleOnEdit,
    isDeleting,
    setCurrentWorkgroup,
    setDeleteOpen,
    updateNameModalOpen,
    workgroupDelete,
    apiLoading,
  } = useWorkgroups();

  const renderAdministrators = (row: WorkGroup) =>
    "--"
  // row.administrators?.length > 0
  //   ? row.administrators.map((admin) => admin.name).join(", ")
  //   : "--";

  const columns = useMemo(() => {
    return [
      {
        name: 'NAME',
        selector: (row: WorkGroup) => <DefaultCell value={row.name} />,
        minWidth: '240px',
        maxWidth: '410px',
      },
      {
        name: 'ADMINISTRATORS',
        selector: (row: WorkGroup) =>
          <Tooltip
            content={renderAdministrators(row)}
            contentClassName={ellipsis}
            place="left"
            key={`administrators-column-${row.id}`}
          >
            {/* {row.administrators?.map((administrator, index) => {
              return (
                <div>{`${administrator.name}${row.administrators.length > index ? "," : ""
                  }`}</div>
              );
            })} */}
            -
          </Tooltip>,
        minWidth: '180px',
        overflow: 'true',
      },
      {
        right: true,
        sortable: false,
        cell: (row: WorkGroup) =>
          <RowActions
            key={`row-actions-${row.id}`}
            workgroup={row}
            handleToggleDelete={(
              open: boolean,
              workgroup: WorkGroup
            ) => {
              setCurrentWorkgroup(workgroup);
              setDeleteOpen(true);
            }}
            canManage={canManageGroupsAndRoles}
            onEdit={handleOnEdit}
          />,
        width: '200px',
      },
    ] as TableColumn<WorkGroup>[];
  }, [canManageGroupsAndRoles, handleOnEdit, setCurrentWorkgroup, setDeleteOpen])

  if (workgroupsLoading)
    return <LoadingScreen />

  if (workgroups.length === 0)
    return (
      <Content horizontalAlign="center" padding={40}>
        No Workgroups
      </Content>
    )

  return (
    <>
      <StyledDataTableWrapper
        data-test-id="table"
        className={`table-container}`}
      >
        <DataTable
          columns={columns}
          data={workgroups}
          defaultSortFieldId={4}
          defaultSortAsc={false}
          customStyles={datatableStyle}
        />
      </StyledDataTableWrapper>

      <GenericModal
        id='delete-modal'
        title="Delete"
        subtitle={
          <Vertical spacing={12}>
            <span className={modalTextStyle}>
              Are you sure you want to permanently delete the &nbsp;
              <strong>{currentWorkgroup?.name} workgroup</strong> and the
              roles associated with it?
            </span>
            <Horizontal verticalAlign="top" spacing={6}>
              <span>
                <icons.InfoIcon fill={colours.secondaryText} />
              </span>
              <span className={modalTextStyle}>
                Please note there are{" "}
                {/* {currentWorkgroup?.administrators.length} */}
                &nbsp; users associated to this workgroup/role. The
                association will be removed if you delete the workgroup.
              </span>
            </Horizontal>
          </Vertical>
        }
        actionName="Delete"
        actionStyle="cancel"
        icon={
          <RoundedIcon>
            <icons.OutlineDeleteIcon height={25} width={25} />
          </RoundedIcon>
        }
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onAction={workgroupDelete}
        loading={isDeleting}
      />
      <EditWorkgroupNameModal
        isOpen={updateNameModalOpen}
        onClose={handleEditOnClose}
        defaultValue={currentWorkgroup?.name || ""}
        onSubmit={(workgroupName: string) => {
          changeWorkgroupName(workgroupName);
        }}
        loading={apiLoading}
      />
    </>
  );
});

export default WorkgroupList;

const modalTextStyle = style({
  color: colours.primaryText,
});

const ellipsis = style({
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
});